import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../../application-core/constants';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import { globalStyles as styles } from '../../core/style/gstheme';
import { CONCEPT_LOAD_OPTIONS } from '../../core/utils/constants';
import { displayDate, sortResources } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import SchemeChip from '../core/SchemeChip';
import IriDisplay from '../taxonomies/IriDisplay';
import TypeDisplay from '../taxonomies/TypeDisplay';

const DEFAULT_COLS = 4;
const DISABLE_RELATION_DELETION = false;

class ConceptDisplay extends React.Component {

  computeLangValues = (prop) => {
    const data = this.props.data;
    const viewLang = this.props.viewLang;
    let values;

    if (!(prop in data)) return [];

    // For ontology properties langstrings will take the @value form
    // For mutiple values is in array form
    // This will also handle rare situation where datatype is changed to lang string form somthing else
    // albeit with no fancy formatting of certain types
    if (Array.isArray(data[prop])) {
      values = data[prop]
        .filter(v => v.language === viewLang || !v.language)
        .map(v => v["@value"] ?
          v["@value"]
          :
          this.computeLiteralValue(v)
        );
    }
    else {
      // For a single non-aliased property value is in object form
      if (data[prop]["@value"]) {
        if (data[prop]["language"] === viewLang) {
          values = [data[prop]["@value"]];
        }
        else values = [];
      }
      else {
        // For aliased props they will take language objects
        values = data[prop][viewLang] ?
          Array.isArray(data[prop][viewLang]) ?
            [...data[prop][viewLang]]
            :
            [data[prop][viewLang]]
          :
          [];
      }
    }
    values.sort((a, b) => {
      if (a.toLowerCase() < b.toLowerCase()) return -1;
      return 1;
    });
    return values;
  };

  computeLiteralValue = (value) => {
    if (value === false) return "FALSE";
    if (value === true) return "TRUE";
    return value.toString();
  };

  computeDatatypeValues = (prop) => {
    const data = this.props.data;
    let values = prop in data ?
      Array.isArray(data[prop]) ?
        [...data[prop].map(dp => dp["@value"] ?
          dp["@value"]
          :
          dp
        )]
        :
        data[prop]["@value"] ?
          [this.computeLiteralValue(data[prop]["@value"])]
          :
          [this.computeLiteralValue(data[prop])]
      :
      [];
    // We convert some results into a more user-friendly output
    if (values && values.length > 0) {
      if (Array.isArray(data[prop])) {
        values = values.map((v, index) => {
          if (data[prop][index].type === "http://www.w3.org/2001/XMLSchema#date") {
            return displayDate(v);
          }
          else if (data[prop][index].type === "http://www.w3.org/2001/XMLSchema#dateTime") {
            return displayDate(v, true, true);
          }
          return v;
        });
      }
      else {
        // Should only be one value
        if (data[prop].type === "http://www.w3.org/2001/XMLSchema#date") {
          values = values.map(v => displayDate(v));
        }
        else if (data[prop].type === "http://www.w3.org/2001/XMLSchema#dateTime") {
          values = values.map(v => displayDate(v, true, true));
        }
      }
    }
    values.sort();
    return values;
  };

  computeRelationValues = (prop, graph) => {
    const data = this.props.data;
    let values = prop in data && data[prop] !== undefined ?
      Array.isArray(data[prop]) ?
        [...data[prop]].map(dp => dp.id || dp)
        :
        [data[prop].id || data[prop]]
      :
      [];
    const sortedValues = sortResources(values, this.props.graph, "prefLabel", this.props.viewLang);
    values = sortedValues.map(v => <IriDisplay
      id={v}
      key={v}
      source={!this.props.editMode || DISABLE_RELATION_DELETION ? null : data.id}
      property={prop}
      propertyGraph={graph}
      components={this.props.taxonomies}
      projectIRI={this.props.project}
      handleNotAvailable={true}
      graph={this.props.graph}
      viewLang={this.props.viewLang}
      defaultLang={this.props.defaultLang} />);
    return values;
  };

  computeOutput = (values, truncate = false) => {
    const output = values.map((label, index) => {
      const outputText = truncate ? label.toString().substring(0, 200) : label.toString();
      return <Typography
        data-test={"value-" + index}
        key={index}
        className={this.props.classes.tableCellText}
        style={{
          ...(index < values.length - 1) && { borderBottom: 'solid 1px #d0d0d0' },
          paddingBottom: 4,
          ...(index > 0) && { paddingTop: 4 }
        }}>
        {outputText
          .split("\n")
          .map((t, index) => <span data-test={"span-" + index} key={index} style={{ display: 'block' }}>{t}</span>)}
        {truncate && label.length >= 200 &&
          <span data-test="truncated" style={{ display: 'inline-block', float: 'right', color: '#ff6600' }}>[Truncated]</span>
        }
      </Typography>;
    });
    return output;
  };

  computeLangStringOutput = (prop) => {
    try {
      GS.handle("CLO");
      const values = this.computeLangValues(prop);
      return this.computeOutput(values, true);
    }
    catch (e) {
      return "Error computing values";
    }
  };

  computeDatatypeOutput = (prop) => {
    try {
      GS.handle("CDO");
      const values = this.computeDatatypeValues(prop);
      return this.computeOutput(values);
    }
    catch (e) {
      return "Error computing values";
    }
  };

  computeRelationOutput = (prop, graph) => {
    try {
      GS.handle("CRO");
      const values = this.computeRelationValues(prop, graph);
      return values;
    }
    catch (e) {
      return "Error computing values";
    }
  };

  render() {

    const { data, classes, viewLang, defaultLang, graph, project, taxonomies, display, handleSelectionChange, canSelect,
      viewOptions, editMode, selected, ontologyProjectAssociation } = this.props;

    const columnDisplay = display ?
      [...display]
      :
      [true, true, true, true];

    // If somebody else has deleted it we will now hide it
    //    if (data.pendingDeletion) return null;

    let label = "No label available";
    const displayLang = viewLang && data.prefLabel && data.prefLabel[viewLang] ?
      viewLang
      :
      defaultLang;

    if (data.prefLabel && data.prefLabel[displayLang]) {
      label = Array.isArray(data.prefLabel[displayLang]) ?
        [...data.prefLabel[displayLang]].sort().join(" AND ")
        :
        data.prefLabel[displayLang];
    }

    const additionalColumns = columnDisplay.slice(DEFAULT_COLS).filter(co => co !== undefined);
    const additionalOptions = viewOptions.slice(DEFAULT_COLS).filter(co => !co.heading);
    const classIRIs = GS.getClassIRIs(data.id, graph);
    const classValues = Array.isArray(data.type) ? data.type.filter(c => c !== "Concept") : [];
    const isDeleted = data.pendingDeletion === true;
    const basicStyle = {
      minWidth: '200px',
      maxWidth: '300px',
      paddingTop: 12,
      ...(isDeleted) && { color: 'white' }
    };

    return (
      <TableRow
        data-test={"concept-" + data.id}        
        classes={{
          root: classes.stripedRow
        }}
        style={{ ...(isDeleted) && { backgroundColor: '#DD2C00' } }}>
        {editMode &&
          <TableCell className={classes.tableCell}>
            <Checkbox
              disabled={!selected && (!canSelect || isDeleted)}
              data-test="select-item"
              size="small"
              color="primary"
              className={classes.checked}
              checked={selected}
              onClick={(ev) => handleSelectionChange(this.props.id, ev.target.checked)} />
          </TableCell>
        }
        {columnDisplay[0] &&
          <TableCell className={classes.tableCell} style={basicStyle}>
            <Typography data-test={displayLang !== viewLang ? "no-lang-label" : "label"} className={classes.tableCellText} style={displayLang !== viewLang ? { color: '#FF6F00' } : {}}>
              {label}
            </Typography>
          </TableCell>
        }
        {columnDisplay[1] &&
          <TableCell className={classes.tableCell} style={basicStyle}>
            {this.computeLangStringOutput("altLabel")}
          </TableCell>
        }
        {columnDisplay[2] &&
          <TableCell className={classes.tableCell} style={{ basicStyle }}>
            <TypeDisplay
              graph={graph}
              wrap={false}
              classIRIs={classIRIs}
              ontologyProjectAssociation={ontologyProjectAssociation}
              isReadOnly={!editMode || DISABLE_RELATION_DELETION}
              defaultLang={defaultLang}
              viewLang={viewLang}
              onAddClass={this.onAddClass}
              projectIRI={project}
              id={data.id}
              values={classValues} />
          </TableCell>
        }
        {columnDisplay[3] &&
          <TableCell className={classes.tableCell} style={basicStyle}>
            <Typography className={classes.tableCellText}>
              {data.id}
            </Typography>
          </TableCell>
        }
        {additionalColumns.map((col, colIndex) => {
          if (col === false) return null;
          return <TableCell
            key={colIndex}
            className={classes.tableCell}
            style={{
              minWidth: '100px',
              maxWidth: '300px',
              paddingTop: 12,
              ...(isDeleted) && { color: 'white' }
            }}>
            {additionalOptions[colIndex].format === "langString" ?
              this.computeLangStringOutput(additionalOptions[colIndex].alias || additionalOptions[colIndex].value)
              :
              additionalOptions[colIndex].format === "datatype" ?
                this.computeDatatypeOutput(additionalOptions[colIndex].value)
                :
                additionalOptions[colIndex].format === "relation" ?
                  this.computeRelationOutput(additionalOptions[colIndex].value, additionalOptions[colIndex].graph)
                  :
                  <Typography className={classes.tableCellText}>
                    <span>Cannot compute value {additionalOptions[colIndex].toString()}</span>
                  </Typography>
            }
          </TableCell>;
        })}
        <TableCell className={classes.tableCell} style={{
          minWidth: '100px',
          maxWidth: '300px',
          paddingTop: 12
        }}>
          {data.inScheme && data.inScheme.sort().map(scheme =>
            <div key={scheme}>
              {taxonomies.some(taxonomy => taxonomy.id === scheme) ?
                <SchemeChip
                  key={scheme}
                  id={scheme}
                  handleNotAvailable={true}
                  defaultLang={defaultLang}
                  isReadOnly={true}
                  graph={graph}
                  link={
                    APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                    + project + "&taxonomy=" + scheme + "&view=concept&concept=" + data.id
                  }
                  property="inScheme" />
                :
                <ExternalResourceLink
                  key={scheme}
                  id={scheme}
                  graph={graph}
                  source={data.id}
                  isReadOnly={true}
                  property="inScheme" />
              }
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }
}

ConceptDisplay.defaultProps = {
  hideInScheme: false,
  showInScheme: false,
  selected: false,
  ontologyProjectAssociation: []
};

ConceptDisplay.propTypes = {
  classes: PropTypes.object,
  canSelect: PropTypes.bool,
  display: PropTypes.array,
  editMode: PropTypes.bool,
  selected: PropTypes.bool,
  viewOptions: PropTypes.array.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  taxonomies: PropTypes.array.isRequired,
  handleSelectionChange: PropTypes.func,
  data: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
};

export default withStyles(styles)(withGS(ConceptDisplay, ["ALL"], null, CONCEPT_LOAD_OPTIONS, true));